import React from 'react';
import type { Lead } from '../../types';
import type { TableColumn, SelectionRect } from '../../types/table';
import { colors } from '../../styles/tokens';
import { TABLE_Z_INDEX } from '../../constants/table';

export interface SelectionMarqueeProps {
  selection: Set<string>;
  leads: Lead[];
  columns: TableColumn[];
  computeSelectionRectangles: () => SelectionRect[];
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Excel/Sheets-style selection marquee overlay
 * Renders a 2px border around selected cells
 */
export const SelectionMarquee: React.FC<SelectionMarqueeProps> = ({
  selection,
  leads,
  columns,
  computeSelectionRectangles,
  containerRef,
}) => {
  if (selection.size === 0) return null;

  const selectionRects = computeSelectionRectangles();
  const container = containerRef.current;
  if (!container) return null;

  return (
    <>
      {selectionRects.map((rect, idx) => {
        // Find actual DOM elements for top-left and bottom-right cells
        const topLeftLead = leads[rect.minRow];
        const bottomRightLead = leads[rect.maxRow];
        const leftCol = columns[rect.minCol];
        const rightCol = columns[rect.maxCol];

        if (!topLeftLead?.id || !bottomRightLead?.id || !leftCol?.key || !rightCol?.key) {
          return null;
        }

        // Query DOM elements using data attributes
        const topLeftCell = container.querySelector(
          `[data-cell-id="${topLeftLead.id}::${leftCol.key}"]`
        );
        const bottomRightCell = container.querySelector(
          `[data-cell-id="${bottomRightLead.id}::${rightCol.key}"]`
        );

        if (!topLeftCell || !bottomRightCell) return null;

        // Get bounding rects
        const topLeftRect = topLeftCell.getBoundingClientRect();
        const bottomRightRect = bottomRightCell.getBoundingClientRect();

        // Get the Table Body div (parent with 'relative' positioning)
        const tableBody = topLeftCell.closest('.bg-gray-50\\/30');
        if (!tableBody) return null;

        const tableBodyRect = tableBody.getBoundingClientRect();

        // Calculate positions relative to Table Body container
        const top = Math.round(topLeftRect.top - tableBodyRect.top);
        const left = Math.round(topLeftRect.left - tableBodyRect.left);
        const right = Math.round(bottomRightRect.right - tableBodyRect.left);
        const bottom = Math.round(bottomRightRect.bottom - tableBodyRect.top);

        const width = right - left;
        const height = bottom - top;

        // Excel-style selection: 2px solid border, light fill
        return (
          <div
            key={`marquee-${idx}`}
            className="absolute rounded-none pointer-events-none box-border"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              width: `${width}px`,
              height: `${height}px`,
              border: `2px solid ${colors.primary[500]}`,
              backgroundColor: 'rgba(59, 130, 246, 0.08)',
              zIndex: TABLE_Z_INDEX.SELECTION_MARQUEE,
            }}
          />
        );
      })}
    </>
  );
};

SelectionMarquee.displayName = 'SelectionMarquee';
